import useAnalytics from 'core/hooks/useAnalytics';
import { useEffect, useRef } from 'react';

import { useAnalyticsFilters } from '../../contexts/AnalyticsFiltersContext';

export type AnalyticsTabInitialTrigger = 'default' | 'tab_click';

type AnalyticsTabTrigger = AnalyticsTabInitialTrigger | 'class_change' | 'period_change';

type ReportedFilters = { classId: string; period: string };

type Params = {
  eventName: string;
  extraPayload?: Record<string, unknown>;
  initialTrigger: AnalyticsTabInitialTrigger;
  /** Filters the tab's data was loaded for; omit it when the event carries no data. */
  loadedFor?: ReportedFilters | null;
};

function getTrigger(lastReported: ReportedFilters | null, classId: string, initialTrigger: AnalyticsTabInitialTrigger): AnalyticsTabTrigger {
  if (!lastReported) return initialTrigger;

  return lastReported.classId === classId ? 'period_change' : 'class_change';
}

function useAnalyticsTabOpenedEvent({ eventName, extraPayload, initialTrigger, loadedFor }: Params): void {
  const { trackEvent } = useAnalytics();
  const { filtersReady, isMockMode, selectedClass, selectedTimePeriod } = useAnalyticsFilters();
  const lastReportedRef = useRef<ReportedFilters | null>(null);
  const classId = selectedClass.value;
  const period = selectedTimePeriod.value;
  // Compared here rather than taken as a flag: right after a filter change the tab still holds the
  // data of the previous filters and is not loading yet, so its counts would go out with the new ones.
  const hasDataForFilters = loadedFor === undefined || (loadedFor?.classId === classId && loadedFor?.period === period);

  useEffect(() => {
    // Until the provider has applied ?class= and ?period= the filters are the defaults, and the
    // switch to the URL ones would be reported as a change the teacher never made.
    if (!filtersReady || !hasDataForFilters) return;

    const lastReported = lastReportedRef.current;
    if (lastReported?.classId === classId && lastReported?.period === period) return;

    lastReportedRef.current = { classId, period };
    trackEvent(eventName, {
      ...extraPayload,
      trigger: getTrigger(lastReported, classId, initialTrigger),
      class_id: classId,
      period,
      mock_mode: isMockMode,
    })
      .then((result: unknown) => {
        // A null result means analytics was not hydrated yet, so the latch reopens for the retry.
        if (result === null) lastReportedRef.current = lastReported;
      })
      .catch((error: unknown) => console.error('Analytics tracking failed:', error));
  }, [classId, eventName, extraPayload, filtersReady, hasDataForFilters, initialTrigger, isMockMode, period, trackEvent]);
}

export default useAnalyticsTabOpenedEvent;
